/**
 * Per-website dashboard (admin).
 * Overzicht: omzet/ROI/daling per site met filters (type/klant/sort) + KPI-strip.
 * Detail: omzet-film + ROI-callout, recente bezoekers-funnel, ranking, en de
 * autoriteit-laag (onze DA/PA/TF/CF + concurrenten boven ons).
 * Data via de backend-endpoints /api/data/site-list, /site-monthly, /site-authority.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceDot,
} from 'recharts';
import { ArrowLeft, Search } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import AuthScreen from './shared/AuthScreen';
import { apiGet } from '../utils/apiClient';
import DashboardNav from './shared/DashboardNav';

const NL = (v) => '€' + Math.round(Number(v || 0)).toLocaleString('nl-NL');
// Rankende pagina kort tonen: pad, of "homepage" als het de root is.
const rankPath = (u) => { try { const p = new URL(u).pathname; return (p === '/' || p === '') ? 'homepage' : p; } catch { return u; } };
const NICHE_LABEL = {
  ontstoppen: 'ontstoppen', 'riool-ontstoppen': 'riool', ontstoppingsdienst: 'dienst',
  loodgieter: 'loodgieter', dakdekker: 'dakdekker',
};
const NICHE_GROUP = (n) => (n === 'riool-ontstoppen' ? 'riool' : n === 'ontstoppingsdienst' ? 'dienst' : n);

// Omzet-periode: welk veld + label hoort bij elke keuze.
const REV_FIELD = { '3m': 'rev_3m', '6m': 'rev_6m', '12m': 'rev_12m', all: 'rev_all', avg: 'avg_monthly_rev' };
const REV_LABEL = { '3m': '3 mnd', '6m': '6 mnd', '12m': '12 mnd', all: 'totaal', avg: 'gem./mnd' };
const revFor = (s, period) => Number(s[REV_FIELD[period] || 'rev_12m'] || 0);
// Backlink-kost (jaarlijks) geëxtrapoleerd naar dezelfde periode als de omzet.
const COST_FRACTION = { '3m': 0.25, '6m': 0.5, '12m': 1, all: 1, avg: 1 / 12 };
const COST_SUFFIX = { '3m': '/3mnd', '6m': '/6mnd', '12m': '/jr', all: '/jr', avg: '/mnd' };
const costFor = (s, period) => Number(s.active_cost_year || 0) * (COST_FRACTION[period] ?? 1);
// Backlink-kost als % van omzet — richtlijn 10% (lead-gen verdedigbaar 5-15%, Perplexity DR jun 2026).
const COST_PCT_TARGET = 0.10;
// Omzet-delta: alleen 3m/6m/12m hebben een vergelijkingsperiode (totaal/gem. niet).
const DELTA_PERIODS = ['3m', '6m', '12m'];

// Waarde per kolom voor sortering (null/'' = leeg -> altijd onderaan). 'rev' volgt de periode.
const SORT_VAL = (s, col, period) => {
  switch (col) {
    case 'dom': return (s.target_domain || '').toLowerCase();
    case 'klant': return (s.klant || '').toLowerCase();
    case 'rev': return revFor(s, period);
    case 'active_cost_year': return Number(s.active_cost_year || 0);
    case 'pct_of_peak': return s.pct_of_peak == null ? null : Number(s.pct_of_peak);
    case 'roi_multiple_12m': return s.roi_multiple_12m == null ? null : Number(s.roi_multiple_12m);
    case 'visibility_pct': return s.visibility_pct == null ? null : Number(s.visibility_pct);
    default: return null;
  }
};
const STATUS_MATCH = (s, st, period) => {
  if (!st) return true;
  if (st === 'geen') return !revFor(s, period);
  if (st === 'daling') return s.pct_of_peak != null && Number(s.pct_of_peak) < 0.6;
  if (st === 'goud') return !s.active_cost_year && Number(s.rev_12m || 0) >= 2000;
  if (st === 'backlinks') return Number(s.active_cost_year || 0) > 0;
  return true;
};
// Sorteer-presets (kolom, richting, label) — gedeeld met de klikbare koppen. 'rev' = gekozen periode.
const SORT_PRESETS = [
  ['pct_of_peak', 'asc', 'grootste daling'],
  ['rev', 'desc', 'hoogste omzet'],
  ['rev', 'asc', 'laagste omzet (€0 eerst)'],
  ['roi_multiple_12m', 'desc', 'hoogste ROI'],
  ['roi_multiple_12m', 'asc', 'laagste ROI'],
  ['active_cost_year', 'desc', 'hoogste backlink-kost'],
];
const DEFAULT_DIR = { dom: 'asc', klant: 'asc', rev: 'desc', pct_of_peak: 'asc', active_cost_year: 'desc', roi_multiple_12m: 'desc', visibility_pct: 'desc' };

function sparkline(arr) {
  if (!arr || arr.length < 2) return null;
  const w = 116, h = 30, p = 3;
  const nums = arr.map(Number);
  const mn = Math.min(...nums), mx = Math.max(...nums), rng = mx - mn || 1;
  const path = nums.map((v, i) => {
    const x = p + (w - 2 * p) * i / (nums.length - 1);
    const y = h - p - (h - 2 * p) * (v - mn) / rng;
    return (i ? 'L' : 'M') + x.toFixed(1) + ' ' + y.toFixed(1);
  }).join(' ');
  const last = nums[nums.length - 1];
  const col = last < mx * 0.4 ? 'var(--color-error)' : last < mx * 0.75 ? 'var(--color-warning)' : 'var(--color-success)';
  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      <path d={path} fill="none" stroke={col} strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

function VsPiek({ pct }) {
  if (pct == null) return <span className="pw-mut">—</span>;
  const p = Math.round(pct * 100);
  const col = p < 40 ? 'var(--color-error)' : p < 75 ? 'var(--color-warning)' : 'var(--color-success)';
  const cls = p < 40 ? 'bad' : p < 75 ? 'warn' : 'good';
  return (
    <span className={`pw-vs ${cls}`}>
      <span className="pw-vsbar"><i style={{ width: `${Math.min(100, p)}%`, background: col }} /></span>{p}%
    </span>
  );
}

// Omzet-delta-pill: ↑/↓ + %. Houdt groen/rood-semantiek (los van de uniform-rode KPI-cards).
function DeltaPill({ pct }) {
  if (pct == null) return <span className="pw-delta none" title="onvoldoende historie">—</span>;
  const cls = Math.abs(pct) < 0.005 ? 'flat' : pct > 0 ? 'up' : 'down';
  const sign = pct > 0 ? '▲' : pct < 0 ? '▼' : '–';
  return <span className={`pw-delta ${cls}`}>{sign} {Math.abs(Math.round(pct * 100))}%</span>;
}

// SEO-zichtbaarheid (0-100, Σ volume×CTR(positie) ÷ potentieel) met balk, à la SERPWatcher.
function Vis({ pct, visits, kws }) {
  if (pct == null) return <span className="pw-mut">—</span>;
  const col = pct >= 70 ? 'var(--color-success)' : pct >= 40 ? 'var(--color-warning)' : 'var(--color-error)';
  const cls = pct >= 70 ? 'good' : pct >= 40 ? 'warn' : 'bad';
  return (
    <span className={`pw-vs ${cls}`} title={`~${Number(visits || 0).toLocaleString('nl-NL')} organische bezoekers/mnd · ${kws || 0} money-keywords`}>
      <span className="pw-vsbar"><i style={{ width: `${Math.min(100, pct)}%`, background: col }} /></span>{pct}%
    </span>
  );
}

function Metric({ label, value, hl, delta }) {
  const d = delta == null || delta === 0 ? null : delta;
  return (
    <div className={`pw-metric ${hl ? 'hl' : ''}`}>
      <div className="pw-ml">{label}</div>
      <div className="pw-mv">{value == null ? '—' : Number(value).toLocaleString('nl-NL')}</div>
      {d != null && <div className={`pw-md ${d > 0 ? 'up' : 'down'}`}>{d > 0 ? `▲ ${d}` : `▼ ${Math.abs(d)}`}</div>}
    </div>
  );
}

const fmtDay = (s) => { try { return new Date(s).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }); } catch { return s; } };

// ---- Gedeelde bouwstenen met het Money-Keywords dashboard (zelfde semantiek + uiterlijk) ----
const NUM = (v) => Number(v || 0).toLocaleString('nl-NL');
const posCls = (p) => (p <= 3 ? 'good' : p <= 10 ? 'warn' : 'bad');

// Beweging t.o.v. de vorige rank-run (delta = pos_vorige - pos_nu; positief = gestegen).
function Delta({ mv }) {
  if (!mv) return null;
  const { delta, status } = mv;
  if (status === 'nieuw in beeld') return <span className="pw-dlt new" title="nieuw in beeld — we ranken hier voor het eerst">✦</span>;
  if (status === 'uit beeld gevallen') return <span className="pw-dlt out" title="uit beeld gevallen — buiten het gemeten venster gezakt">✗</span>;
  if (delta == null || status === 'eerste meting') return null;
  if (delta > 0) return <span className="pw-dlt up">▲{delta}</span>;
  if (delta < 0) return <span className="pw-dlt down">▼{Math.abs(delta)}</span>;
  return null;
}

// 8wk positie-verloop. Vaste, gedeelde top-zware schaal (#1 boven), identiek aan het Money-Keywords
// dashboard: stijging = groene lijn omhoog, daling = rode lijn omlaag, geen mutatie = vlakke lijn.
const SPARK_CAP = 20;
function Trend({ hist }) {
  const v = (Array.isArray(hist) ? hist : []).filter((x) => x != null).map(Number);
  if (v.length < 2) return <span className="pw-rkmut">—</span>;
  const w = 58, h = 22, pad = 3;
  const frac = (p) => Math.log(Math.min(Math.max(p, 1), SPARK_CAP)) / Math.log(SPARK_CAP);
  const px = (i) => (i / (v.length - 1)) * w;
  const py = (p) => pad + frac(p) * (h - 2 * pad);
  const pts = v.map((p, i) => `${px(i).toFixed(1)},${py(p).toFixed(1)}`).join(' ');
  const net = v[v.length - 1] - v[0];
  const up = net < 0, down = net > 0;
  const col = up ? 'var(--color-success)' : down ? '#e11d48' : 'var(--color-neutral-300)';
  const lx = px(v.length - 1), ly = py(v[v.length - 1]);
  const tip = up ? `gestegen ${Math.abs(net)} ${Math.abs(net) === 1 ? 'positie' : 'posities'}`
    : down ? `gedaald ${Math.abs(net)} ${Math.abs(net) === 1 ? 'positie' : 'posities'}` : 'geen verandering';
  return (
    <svg className="pw-spark" width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img" aria-label={tip}>
      <title>{tip}</title>
      <polyline points={pts} fill="none" stroke={col} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lx.toFixed(1)} cy={ly.toFixed(1)} r="1.9" fill={col} />
    </svg>
  );
}

const DIAG = {
  'on-page': { label: 'on-page', cls: 'onpage', tip: 'Wij overtreffen de top-3 op links — de rem zit in on-page/relevantie/lokaal, niet in autoriteit. Links kopen helpt hier niet.' },
  'link-gap': { label: 'links', cls: 'linkgap', tip: 'Concurrent boven ons heeft een sterker linkprofiel (vaak Trust Flow) — hier wél (hoogwaardige) links bouwen.' },
  'niet-in-serp': { label: 'buiten top-10', cls: 'oos', tip: 'Onze site staat niet in de opgehaalde top-10, dus geen eigen metrics in deze run.' },
};
function Diag({ d }) {
  if (!d) return <span className="pw-rkmut">—</span>;
  const x = DIAG[d] || { label: d, cls: '', tip: '' };
  return <span className={`pw-diag ${x.cls}`} title={x.tip}>{x.label}</span>;
}

export default function PerWebsiteDashboard() {
  const auth = useAuth('admin');
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [fNiche, setFNiche] = useState('');
  const [fKlant, setFKlant] = useState('');
  const [status, setStatus] = useState('');
  const [period, setPeriod] = useState('12m');
  const [deltaMode, setDeltaMode] = useState('prev'); // 'prev' = vorige gelijke periode, 'yoy' = vorig jaar
  const [sortCol, setSortCol] = useState('pct_of_peak');
  const [sortDir, setSortDir] = useState('asc');
  const [sel, setSel] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [boardByKw, setBoardByKw] = useState(null); // map keyword -> board-rij (economics + mv + hist + auth)

  useEffect(() => {
    if (!auth.authenticated) return;
    setLoading(true);
    apiGet('/data/site-list')
      .then((d) => { setSites(Array.isArray(d) ? d : []); setLoading(false); })
      .catch((e) => { setErr(String(e.message || e)); setLoading(false); });
  }, [auth.authenticated]);

  const openSite = useCallback(async (domain) => {
    setDetailLoading(true);
    setSel({ site: domain });
    try {
      const [m, a] = await Promise.all([
        apiGet('/data/site-monthly', { site: domain }),
        apiGet('/data/site-authority', { site: domain }),
      ]);
      setSel({ site: domain, ...m, authority: a });
    } catch (e) { setErr(String(e.message || e)); }
    setDetailLoading(false);
    window.scrollTo(0, 0);
  }, []);

  // De money-keyword economics (EV/CR/EL/EO/Up + beweging + 8wk + diagnose) komen uit dezelfde board
  // als het Money-Keywords dashboard. Eén keer ophalen zodra de eerste site geopend wordt, dan gecachet.
  useEffect(() => {
    if (!sel || boardByKw) return;
    apiGet('/data/keyword-list')
      .then((d) => {
        const map = {};
        for (const r of (Array.isArray(d) ? d : [])) map[r.keyword] = r;
        setBoardByKw(map);
      })
      .catch(() => setBoardByKw({})); // faal stil: tabel toont economics dan als —
  }, [sel, boardByKw]);

  const klanten = useMemo(
    () => [...new Set(sites.map((s) => s.klant).filter(Boolean))].sort(),
    [sites],
  );

  const rows = useMemo(() => {
    const qq = q.trim().toLowerCase();
    let r = sites.filter((s) =>
      (!qq || s.target_domain.toLowerCase().includes(qq))
      && (!fNiche || NICHE_GROUP(s.niche) === fNiche)
      && (!fKlant || s.klant === fKlant)
      && STATUS_MATCH(s, status, period));
    const dir = sortDir === 'asc' ? 1 : -1;
    r = [...r].sort((a, b) => {
      const va = SORT_VAL(a, sortCol, period), vb = SORT_VAL(b, sortCol, period);
      const an = va == null || va === '', bn = vb == null || vb === '';
      if (an && bn) return 0;
      if (an) return 1;   // leeg/onbekend altijd onderaan
      if (bn) return -1;
      if (typeof va === 'string') return va.localeCompare(vb) * dir;
      return (va - vb) * dir;
    });
    return r;
  }, [sites, q, fNiche, fKlant, status, period, sortCol, sortDir]);

  const kpis = useMemo(() => {
    const sum = (f) => rows.reduce((a, s) => a + Number(s[f] || 0), 0);
    const totRev = rows.reduce((a, s) => a + revFor(s, period), 0);
    const totCost = rows.reduce((a, s) => a + costFor(s, period), 0);
    const dalers = rows.filter((s) => s.pct_of_peak != null && Number(s.pct_of_peak) < 0.6).length;
    const goud = rows.filter((s) => !s.active_cost_year && Number(s.rev_12m || 0) >= 2000).length;
    // Omzet-delta: huidige periode vs vergelijking (vorige gelijke periode of YoY). Kalender-correct
    // uit de view-velden. cmp<=0 (geen historie) -> null, dan toont de pill "—".
    const s3 = sum('rev_3m'), s6 = sum('rev_6m'), s12 = sum('rev_12m'), s24 = sum('rev_24m');
    const cmpPrev = { '3m': s6 - s3, '6m': s12 - s6, '12m': s24 - s12 }[period];
    const cmpYoy = { '3m': sum('yoy_3m'), '6m': sum('yoy_6m'), '12m': s24 - s12 }[period];
    const cmp = deltaMode === 'yoy' ? cmpYoy : cmpPrev;
    const deltaPct = (DELTA_PERIODS.includes(period) && cmp > 0) ? (totRev - cmp) / cmp : null;
    // Backlink-kost als % van de omzet over dezelfde view + periode.
    const costPct = totRev > 0 ? totCost / totRev : null;
    return { totRev, totCost, dalers, goud, deltaPct, costPct };
  }, [rows, period, deltaMode]);

  if (auth.loading) return <div className="pw"><style>{PW_CSS}</style><div className="pw-load">Laden…</div></div>;
  if (!auth.authenticated) return <AuthScreen auth={auth} title="LEAD MAGNATE" subtitle="Per website" error={err} />;

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(col); setSortDir(DEFAULT_DIR[col] || 'desc'); }
  };
  const arr = (col) => (sortCol === col ? <span className="pw-sortarr">{sortDir === 'asc' ? ' ▲' : ' ▼'}</span> : null);
  const sortKey = `${sortCol}|${sortDir}`;

  const chartData = sel && sel.monthly ? sel.monthly.map((m) => ({
    m: m.month_date ? m.month_date.slice(0, 7) : '',
    omzet: Number(m.revenue || 0), calls: m.total_calls || 0,
  })) : [];
  const peak = chartData.reduce((mx, d) => (d.omzet > (mx ? mx.omzet : -1) ? d : mx), null);
  const sum = sel && sel.summary;
  const fun = sel && sel.funnel;
  const authKws = sel && sel.authority && sel.authority.keywords ? sel.authority.keywords : [];
  const primary = authKws.find((k) => k.own) || authKws[0] || null;
  const da = sel && sel.authority && sel.authority.domain_authority ? sel.authority.domain_authority : null;

  return (
    <div className="pw">
      <style>{PW_CSS}</style>
      <DashboardNav active="website" />
      <div className="pw-top"><div className="pw-top-in">
        <div className="pw-brand"><div className="pw-logo">LM</div><span className="pw-bt">LEAD MAGNATE</span>
          <span className="pw-crumb">› Per website</span></div>
      </div></div>

      <div className="pw-wrap">
        {err && <div className="pw-err">{err}</div>}

        {/* ============ OVERZICHT ============ */}
        {!sel && (
          <>
            <div className="pw-periodbar">
              <span className="pw-periodlabel">Omzet-periode</span>
              <div className="pw-periods">
                {[['3m', '3 mnd'], ['6m', '6 mnd'], ['12m', '12 mnd'], ['all', 'Totaal'], ['avg', 'Gem./mnd']].map(([k, l]) => (
                  <button key={k} className={period === k ? 'on' : ''} onClick={() => setPeriod(k)}>{l}</button>
                ))}
              </div>
              {DELTA_PERIODS.includes(period) && (
                <div className="pw-vsswitch">
                  {[['prev', 'vs vorige periode'], ['yoy', 'vs vorig jaar']].map(([k, l]) => (
                    <button key={k} className={deltaMode === k ? 'on' : ''} onClick={() => setDeltaMode(k)}>{l}</button>
                  ))}
                </div>
              )}
            </div>
            <div className="pw-kpis">
              <div className="pw-kpi">
                <div className="pw-k">Omzet {REV_LABEL[period]}</div>
                <div className="pw-v">{NL(kpis.totRev)}{DELTA_PERIODS.includes(period) && <DeltaPill pct={kpis.deltaPct} />}</div>
                <div className="pw-s">{rows.length} sites{DELTA_PERIODS.includes(period) && ` · ${deltaMode === 'yoy' ? 'vs vorig jaar' : 'vs vorige periode'}`}</div>
              </div>
              <div className="pw-kpi">
                <div className="pw-k">In daling</div><div className="pw-v">{kpis.dalers}</div><div className="pw-s">onder 60% van piek</div>
              </div>
              <div className="pw-kpi">
                <div className="pw-k">Goudmijnen</div><div className="pw-v">{kpis.goud}</div><div className="pw-s">omzet, geen backlinks</div>
              </div>
              <div className="pw-kpi">
                <div className="pw-k">Backlink-kost {REV_LABEL[period]}</div>
                <div className="pw-v">{NL(kpis.totCost)}</div>
                <div className="pw-s">
                  {kpis.costPct == null ? '—'
                    : <><span className={`pw-costpct ${kpis.costPct <= COST_PCT_TARGET ? 'good' : kpis.costPct <= 0.15 ? 'warn' : 'bad'}`}>{(kpis.costPct * 100).toFixed(1)}%</span> van omzet</>}
                </div>
              </div>
            </div>

            <div className="pw-filters">
              <div className="pw-search">
                <Search size={16} />
                <input placeholder="Zoek website…" value={q} onChange={(e) => setQ(e.target.value)} />
              </div>
              <select className="pw-sel" value={fNiche} onChange={(e) => setFNiche(e.target.value)}>
                <option value="">Alle types</option>
                <option value="ontstoppen">Ontstoppen</option>
                <option value="riool">Riool ontstoppen</option>
                <option value="dienst">Ontstoppingsdienst / bedrijf</option>
                <option value="loodgieter">Loodgieter</option>
                <option value="dakdekker">Dakdekker</option>
              </select>
              <select className="pw-sel" value={fKlant} onChange={(e) => setFKlant(e.target.value)}>
                <option value="">Alle klanten</option>
                {klanten.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
              <select className="pw-sel" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">Status: alle</option>
                <option value="geen">Geen omzet (€0)</option>
                <option value="daling">In daling</option>
                <option value="goud">Goudmijnen (geen backlinks)</option>
                <option value="backlinks">Met backlinks</option>
              </select>
              <select
                className="pw-sel"
                value={SORT_PRESETS.some(([c, d]) => `${c}|${d}` === sortKey) ? sortKey : ''}
                onChange={(e) => { if (e.target.value) { const [c, d] = e.target.value.split('|'); setSortCol(c); setSortDir(d); } }}
              >
                {!SORT_PRESETS.some(([c, d]) => `${c}|${d}` === sortKey) && <option value="">Sorteer: aangepast</option>}
                {SORT_PRESETS.map(([c, d, label]) => <option key={`${c}|${d}`} value={`${c}|${d}`}>Sorteer: {label}</option>)}
              </select>
              <span className="pw-count">{loading ? 'laden…' : `${rows.length} sites`}</span>
            </div>

            <div className="pw-panel">
              <div className="pw-scroll">
              <table className="pw-table">
                <thead><tr>
                  <th className="pw-sortable" title="Website" onClick={() => toggleSort('dom')}>Site{arr('dom')}</th>
                  <th className="pw-sortable" title="Klant" onClick={() => toggleSort('klant')}>Klant{arr('klant')}</th>
                  <th className="pw-sortable r" title={`Omzet ${REV_LABEL[period]}`} onClick={() => toggleSort('rev')}>€ {REV_LABEL[period]}{arr('rev')}</th>
                  <th className="pw-sortable c" title="Verloop &amp; piek t.o.v. eigen hoogste maand" onClick={() => toggleSort('pct_of_peak')}>Trend{arr('pct_of_peak')}</th>
                  <th className="pw-sortable r" title="Backlink-kosten per jaar" onClick={() => toggleSort('active_cost_year')}>BL{arr('active_cost_year')}</th>
                  <th className="pw-sortable r" title="ROI (12 mnd omzet ÷ backlink-kosten)" onClick={() => toggleSort('roi_multiple_12m')}>ROI{arr('roi_multiple_12m')}</th>
                  <th className="pw-sortable c" title="SEO-zichtbaarheid (volume × CTR)" onClick={() => toggleSort('visibility_pct')}>SEO{arr('visibility_pct')}</th>
                  <th />
                </tr></thead>
                <tbody>
                  {rows.map((s) => (
                    <tr key={s.target_domain} onClick={() => openSite(s.target_domain)}>
                      <td><span className="pw-dom">{s.target_domain}
                        <span className={`pw-chip ${NICHE_GROUP(s.niche)}`}>{NICHE_LABEL[s.niche] || s.niche || '—'}</span></span></td>
                      <td className="pw-klant">{s.klant || '—'}</td>
                      <td className="r"><span className="pw-money">{NL(revFor(s, period))}</span></td>
                      <td className="c">{sparkline(s.spark12)}<div style={{ marginTop: 2 }}><VsPiek pct={s.pct_of_peak} /></div></td>
                      <td className="r">
                        {s.active_cost_year
                          ? <span className="pw-tnum">{NL(costFor(s, period))}<small style={{ color: 'var(--color-neutral-400)' }}> {COST_SUFFIX[period]}</small>{s.inactive_links > 0 && <small className="pw-former" title={`${s.inactive_links} verlopen links · ~${NL(s.inactive_cost_year)}/jr`}> +{s.inactive_links}⟲</small>}</span>
                          : s.total_links > 0
                            ? <span className="pw-former" title={`${s.inactive_links} verlopen links · ~${NL(s.inactive_cost_year)}/jr om terug te kopen`}>⟲ {NL(s.inactive_cost_year)}<small> /jr</small></span>
                            : <span className="pw-mut">—</span>}
                      </td>
                      <td className="r">{s.roi_multiple_12m ? <span className={`pw-roi ${s.roi_multiple_12m >= 5 ? 'good' : 'warn'}`}>{s.roi_multiple_12m}×</span> : <span className="pw-mut">—</span>}</td>
                      <td className="c"><Vis pct={s.visibility_pct} visits={s.est_visits_mnd} kws={s.seo_keywords} /></td>
                      <td className="pw-arrow">›</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              {!loading && rows.length > 0 && <div className="pw-swipe">Veeg de tabel horizontaal voor alle kolommen →</div>}
              {!loading && rows.length === 0 && <div className="pw-note">Geen sites gevonden.</div>}
            </div>
          </>
        )}

        {/* ============ DETAIL ============ */}
        {sel && (
          <>
            <button className="pw-back" onClick={() => setSel(null)}><ArrowLeft size={15} /> terug naar overzicht</button>
            <div className="pw-dhead"><h1>{sel.site}</h1>
              {sum && <span className={`pw-chip ${NICHE_GROUP(sum.niche)}`}>{NICHE_LABEL[sum.niche] || sum.niche}</span>}</div>

            {detailLoading && <div className="pw-note">Detail laden…</div>}

            {sum && (
              <div className="pw-callout">
                <div className="pw-lead">
                  Piek <b className="peak">{NL(sum.peak_month_rev)}/mnd</b> → laatste 3 mnd gem. <b className="now">{NL((sum.rev_3m || 0) / 3)}/mnd</b>
                  {sum.pct_of_peak != null && <> ({Math.round(sum.pct_of_peak * 100)}% van piek)</>}.
                </div>
                <div className="pw-stat"><span className="k">ROI 12 mnd</span><span className="v">{sum.roi_multiple_12m ? `${sum.roi_multiple_12m}×` : '—'}</span></div>
                <div className="pw-stat"><span className="k">Backlink-kost</span><span className="v">{sum.active_cost_year ? `${NL(sum.active_cost_year)}/jr` : 'geen'}</span></div>
                {sum.inactive_links > 0 && (
                  <div className="pw-stat" title={`Voormalige links, ~${NL(sum.inactive_cost_year)}/jr om terug te kopen${sum.avg_source_da_inactive ? ` · gem. bron-DA ${sum.avg_source_da_inactive}` : ''}`}>
                    <span className="k">Verlopen links</span>
                    <span className="v" style={{ color: 'var(--color-warning)' }}>{sum.inactive_links} · {NL(sum.inactive_cost_year)}/jr</span>
                  </div>
                )}
                <div className="pw-stat"><span className="k">Omzet 12 mnd</span><span className="v">{NL(sum.rev_12m)}</span></div>
              </div>
            )}

            {/* recente bezoekers-funnel */}
            {fun && (
              <div className="pw-card" style={{ marginBottom: 18 }}>
                <h3>Recente funnel <span className="pw-src">server-side tracking · sinds {fun.since}</span></h3>
                <div className="pw-funnel">
                  {(() => {
                    const pct = (a) => (fun.visitors > 0 ? Math.round((a / fun.visitors) * 1000) / 10 : null);
                    const cp = pct(fun.calls), lp = pct(fun.leads);
                    const num = (v) => Number(v || 0).toLocaleString('nl-NL');
                    const steps = [
                      { k: 'Bezoekers', v: num(fun.visitors) },
                      { k: 'Calls', v: num(fun.calls), sub: `${num(fun.clicks)} via tel-klik${cp != null ? ` · ${cp}% van bezoek` : ''}` },
                      { k: 'Leads', v: num(fun.leads), sub: lp != null ? `${lp}% van bezoekers` : '', hl: true },
                    ];
                    return (<>
                      {steps.map((s) => (
                        <React.Fragment key={s.k}>
                          <div className="pw-fstep"><div className="pw-fv">{s.v}</div><div className="pw-fk">{s.k}</div>{s.sub && <div className={`pw-fsub${s.hl ? ' hl' : ''}`}>{s.sub}</div>}</div>
                          <div className="pw-farrow">›</div>
                        </React.Fragment>
                      ))}
                      <div className="pw-fstep"><div className="pw-fv" style={{ color: 'var(--color-brand-600)' }}>{NL(fun.revenue)}</div><div className="pw-fk">Omzet</div></div>
                    </>);
                  })()}
                </div>
              </div>
            )}

            {/* Omzet & calls — volledige breedte */}
            <div className="pw-card" style={{ marginBottom: 18 }}>
              <h3>Omzet &amp; calls per maand
                <span className="pw-legend"><span><i style={{ background: 'var(--color-brand-600)' }} />omzet</span><span><i style={{ background: 'var(--color-info)' }} />calls</span></span>
              </h3>
              <div style={{ width: '100%', height: 280 }}>
                <ResponsiveContainer>
                  <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: -4, bottom: 0 }}>
                    <defs><linearGradient id="pwRev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--color-brand-600)" stopOpacity={0.2} />
                      <stop offset="100%" stopColor="var(--color-brand-600)" stopOpacity={0} />
                    </linearGradient></defs>
                    <CartesianGrid stroke="var(--color-neutral-100)" vertical={false} />
                    <XAxis dataKey="m" tick={{ fontSize: 10, fill: 'var(--color-neutral-400)' }} interval="preserveStartEnd" minTickGap={48} />
                    <YAxis yAxisId="l" tick={{ fontSize: 10, fill: 'var(--color-neutral-400)' }} width={52} tickFormatter={(v) => '€' + v} />
                    <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10, fill: 'var(--color-neutral-300)' }} width={30} />
                    <Tooltip formatter={(v, n) => (n === 'omzet' ? NL(v) : v)} labelStyle={{ fontFamily: 'var(--font-mono)' }} />
                    <Area yAxisId="l" type="monotone" dataKey="omzet" stroke="var(--color-brand-600)" strokeWidth={2} fill="url(#pwRev)" />
                    <Line yAxisId="r" type="monotone" dataKey="calls" stroke="var(--color-info)" strokeWidth={1.5} dot={false} strokeDasharray="3 3" />
                    {peak && <ReferenceDot yAxisId="l" x={peak.m} y={peak.omzet} r={4} fill="var(--color-brand-600)" stroke="none" />}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Money-keywords & ranking — volledige breedte, pariteit met het Money-Keywords dashboard.
                Op de site-pagina is het domein bekend, dus de Pos-kolom toont de rankende PAGINA i.p.v.
                de domeinnaam. Economics (EV/CR/EL/EO/Up) + beweging + 8wk + diagnose uit de board. */}
            <div className="pw-card">
              <h3>Money-keywords &amp; ranking
                <span className="pw-src">positie &amp; pagina · beweging · bezoekers · omzet per keyword</span>
              </h3>
              {authKws.length === 0 && <div className="pw-note" style={{ textAlign: 'left', margin: 0 }}>Nog geen ranking-data voor deze site.</div>}
              {authKws.length > 0 && (
                <div className="pw-scroll">
                  <table className="pw-kwtbl">
                    <thead><tr>
                      <th title="Money-keyword">Keyword</th>
                      <th title="Onze positie + beweging t.o.v. de vorige run, en de pagina die rankt">Positie &amp; pagina</th>
                      <th className="c" title="Positie-verloop (laatste rank-runs) — omhoog = beter">8wk</th>
                      <th className="r" title="Maandelijks zoekvolume">Vol</th>
                      <th className="r" title="Est. bezoekers/mnd — zoekvolume × CTR">EV</th>
                      <th className="r" title="Conversie bezoeker→lead (server-side tracking, 30d)">CR</th>
                      <th className="r" title="Est. leads/mnd">EL</th>
                      <th className="r" title="Est. omzet/mnd">EO</th>
                      <th className="r" title="Upside naar #1 per maand">Up</th>
                      <th className="c" title="Authority-diagnose: on-page = wij out-linken de top-3 (fix de pagina); links = concurrent sterker linkprofiel (bouw links)">Dx</th>
                    </tr></thead>
                    <tbody>
                      {authKws.map((k) => {
                        const b = boardByKw ? boardByKw[k.keyword] : null;
                        const pos = k.monitor_pos;
                        return (
                          <tr key={k.keyword}>
                            <td className="pw-rkkw">{k.keyword}</td>
                            <td>
                              <div className="pw-poscell">
                                <div className="pw-posline">
                                  {pos
                                    ? <span className={`pw-pos ${posCls(pos)}`}>#{pos}</span>
                                    : <span className="pw-rkmut">niet in top</span>}
                                  {b && <Delta mv={b.mv} />}
                                </div>
                                {k.monitor_url
                                  ? <a className="pw-pagina" href={k.monitor_url} target="_blank" rel="noopener noreferrer" title={k.monitor_url}>{rankPath(k.monitor_url)}</a>
                                  : <span className="pw-rkmut">—</span>}
                              </div>
                            </td>
                            <td className="c"><Trend hist={b && b.hist} /></td>
                            <td className="r pw-tnum">{NUM(k.search_volume)}</td>
                            <td className="r pw-tnum">{b ? NUM(b.est_visits_mnd) : <span className="pw-rkmut">—</span>}</td>
                            <td className="r"><span className="pw-conv">{b && b.conversie_pct != null ? `${b.conversie_pct}%` : '—'}</span></td>
                            <td className="r pw-tnum">{b ? Number(b.est_leads_mnd || 0).toLocaleString('nl-NL') : <span className="pw-rkmut">—</span>}</td>
                            <td className="r pw-tnum" title={b && b.lead_prijs != null ? `€${Number(b.lead_prijs).toLocaleString('nl-NL')} per lead` : ''}>{b ? NL(b.est_omzet_mnd) : <span className="pw-rkmut">—</span>}</td>
                            <td className="r">{b ? <span className="pw-upside">{NL(b.upside_omzet_top1)}</span> : <span className="pw-rkmut">—</span>}</td>
                            <td className="c"><Diag d={b && b.auth ? b.auth.diagnose : null} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* autoriteit */}
            {(primary || da) && (
              <div className="pw-card" style={{ marginTop: 18 }}>
                <h3>Onze autoriteit</h3>
                {da ? (
                  <>
                    <div className="pw-subh">Homepage-autoriteit{da.prev_captured_at ? <> <span className="pw-src">· groei sinds {fmtDay(da.prev_captured_at)}</span></> : null}</div>
                    <div className="pw-metrics">
                      <Metric label="DA" value={da.metrics.da} delta={da.delta.da} hl />
                      <Metric label="PA" value={da.metrics.pa} delta={da.delta.pa} />
                      <Metric label="TF" value={da.metrics.tf} delta={da.delta.tf} />
                      <Metric label="CF" value={da.metrics.cf} delta={da.delta.cf} />
                      <Metric label="Ref dom." value={da.metrics.ref_domains} delta={da.delta.ref_domains} />
                      <Metric label="Ref IP" value={da.metrics.ref_ips} delta={da.delta.ref_ips} />
                      <Metric label="Backlinks" value={da.metrics.backlinks} delta={da.delta.backlinks} />
                    </div>
                    <div className="pw-amETA">gemeten {fmtDay(da.captured_at)} · bron {da.source === 'serp' ? 'SERP-analyse' : 'url-metrics'}{!da.is_homepage ? ' · subpagina (geen homepage-meting)' : ''}</div>
                  </>
                ) : (
                  <div className="pw-note" style={{ textAlign: 'left', margin: '0 0 14px' }}>
                    Nog geen eigen autoriteits-meting voor deze site (komt automatisch via de dagelijkse SEO-keten).
                  </div>
                )}
                {primary && (primary.competitors || []).length > 0 && <div className="pw-subh" style={{ marginTop: 16 }}>Wie staat boven je <span className="pw-src">· "{primary.keyword}"</span></div>}
                {primary && (primary.competitors || []).length === 0 && <div className="pw-note" style={{ textAlign: 'left', margin: '12px 0 0' }}>Geen echte concurrenten boven ons.</div>}
                {primary && (primary.competitors || []).length > 0 && (
                  <div className="pw-scroll">
                  <table className="pw-comp">
                    <thead><tr><th>#</th><th>Concurrent</th><th className="r">DA</th><th className="r">PA</th><th className="r">TF</th><th className="r">CF</th><th className="r">Ref</th><th className="r">BL</th></tr></thead>
                    <tbody>
                      {primary.competitors.map((c) => (
                        <tr key={c.domain + c.rank}>
                          <td className="pw-tnum">{c.rank}</td>
                          <td>{c.domain}{c.da <= 15 && <span className="pw-weak">zwak</span>}</td>
                          <td className="r pw-tnum" style={c.da <= 15 ? { color: 'var(--color-success)', fontWeight: 700 } : {}}>{c.da}</td>
                          <td className="r pw-tnum">{c.pa}</td><td className="r pw-tnum">{c.tf}</td><td className="r pw-tnum">{c.cf}</td>
                          <td className="r pw-tnum">{c.ref}</td><td className="r pw-tnum">{c.bl}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const PW_CSS = `
.pw{font-family:var(--font-display);background:var(--surface-sunken);min-height:100vh;color:var(--color-neutral-800)}
.pw-load{padding:60px;text-align:center;color:var(--color-neutral-500)}
.pw-top{background:linear-gradient(135deg,var(--color-brand-600),var(--color-brand-800));color:#fff}
.pw-top-in{max-width:84rem;margin:0 auto;padding:14px 24px;display:flex;align-items:center;justify-content:space-between}
.pw-brand{display:flex;align-items:center;gap:13px}
.pw-logo{width:30px;height:30px;border-radius:7px;background:#fff;color:var(--color-brand-600);display:flex;align-items:center;justify-content:center;font-weight:800;font-size:14px}
.pw-bt{font-weight:800;font-size:15px;letter-spacing:-.01em}
.pw-crumb{font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:.08em;color:#fff}
.pw-wrap{max-width:84rem;margin:0 auto;padding:26px 24px}
.pw-err{background:var(--color-error-light);border:1px solid var(--color-error);color:var(--color-error);padding:10px 14px;border-radius:8px;margin-bottom:16px;font-size:13px}
.pw-periodbar{display:flex;align-items:center;gap:12px;margin-bottom:14px}
.pw-periodlabel{font-size:11px;font-weight:700;color:var(--color-neutral-500);text-transform:uppercase;letter-spacing:.08em}
.pw-periods{display:inline-flex;gap:2px;background:var(--color-neutral-100);padding:3px;border-radius:8px}
.pw-periods button{border:none;background:transparent;font-family:var(--font-display);font-size:12px;font-weight:600;color:var(--color-neutral-500);padding:6px 12px;border-radius:6px;cursor:pointer}
.pw-periods button.on{background:var(--surface-raised);color:var(--color-neutral-900);box-shadow:var(--shadow-sm)}
.pw-periods button:hover:not(.on){color:var(--color-neutral-700)}
.pw-kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:20px}
@media(max-width:760px){.pw-kpis{grid-template-columns:repeat(2,1fr)}}
.pw-kpi{position:relative;background:var(--surface);border:1px solid var(--color-neutral-100);border-radius:8px;padding:16px 18px;overflow:hidden;text-align:center}
.pw-kpi::before{content:'';position:absolute;top:0;left:0;right:0;height:3px;background:linear-gradient(90deg,var(--color-brand-600),var(--color-brand-400))}
.pw-kpi .pw-k{font-size:10.5px;font-weight:700;color:var(--color-neutral-500);text-transform:uppercase;letter-spacing:.1em}
.pw-kpi .pw-v{display:flex;align-items:baseline;justify-content:center;gap:9px;font-family:var(--font-mono);font-size:23px;font-weight:600;color:var(--color-neutral-900);margin-top:3px}
.pw-costpct{font-weight:700}.pw-costpct.good{color:var(--color-success)}.pw-costpct.warn{color:var(--color-warning)}.pw-costpct.bad{color:var(--color-error)}
.pw-kpi .pw-s{font-size:11px;color:var(--color-neutral-400);font-weight:500;margin-top:1px}
.pw-delta{font-family:var(--font-display);font-size:12px;font-weight:700;padding:1px 7px;border-radius:999px;letter-spacing:0;white-space:nowrap}
.pw-delta.up{color:var(--color-success);background:var(--color-success-light,#dcfce7)}
.pw-delta.down{color:var(--color-error);background:var(--color-error-light,#fee2e2)}
.pw-delta.flat,.pw-delta.none{color:var(--color-neutral-400);background:var(--color-neutral-100)}
.pw-vsswitch{display:inline-flex;gap:2px;background:var(--color-neutral-100);padding:3px;border-radius:8px;margin-left:auto}
.pw-vsswitch button{border:none;background:transparent;font-family:var(--font-display);font-size:11px;font-weight:600;color:var(--color-neutral-500);padding:5px 10px;border-radius:6px;cursor:pointer}
.pw-vsswitch button.on{background:var(--surface-raised);color:var(--color-neutral-900);box-shadow:var(--shadow-sm)}
.pw-vsswitch button:hover:not(.on){color:var(--color-neutral-700)}
.pw-filters{display:flex;align-items:center;gap:12px;flex-wrap:wrap;margin-bottom:18px}
.pw-search{flex:1;min-width:220px;position:relative;display:flex;align-items:center}
.pw-search svg{position:absolute;left:12px;color:var(--color-neutral-400)}
.pw-search input{width:100%;border:1px solid var(--color-neutral-200);border-radius:6px;padding:10px 14px 10px 36px;font-family:var(--font-display);font-size:14px;color:var(--color-neutral-800)}
.pw-search input:focus{outline:none;border-color:var(--color-brand-500);box-shadow:0 0 0 3px var(--color-brand-100)}
.pw-sel{appearance:none;background:var(--surface);border:1px solid var(--color-neutral-200);border-radius:6px;padding:10px 32px 10px 13px;font-family:var(--font-display);font-size:14px;font-weight:500;color:var(--color-neutral-700);cursor:pointer;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 11px center}
.pw-sel:focus{outline:none;border-color:var(--color-brand-500)}
.pw-count{font-size:12px;color:var(--color-neutral-500);font-weight:600;margin-left:auto}
.pw-panel{background:var(--surface);border:1px solid var(--color-neutral-100);border-radius:8px;overflow:hidden}
.pw-table{width:100%;border-collapse:separate;border-spacing:0}
.pw-table thead{background:linear-gradient(180deg,var(--color-neutral-50),var(--color-neutral-100))}
.pw-table th{padding:12px 16px;font-size:11px;font-weight:700;color:var(--color-neutral-500);text-transform:uppercase;letter-spacing:.1em;text-align:left;border-bottom:2px solid var(--color-neutral-200);white-space:nowrap}
.pw-table th.r,.pw-table td.r{text-align:right}
.pw-table th.c,.pw-table td.c{text-align:center}
.pw-table td{padding:12px 16px;font-size:14px;color:var(--color-neutral-700);border-bottom:1px solid var(--color-neutral-100);font-variant-numeric:tabular-nums}
.pw-table tbody tr{cursor:pointer;transition:background .15s}
.pw-table tbody tr:hover{background:var(--row-hover)}
.pw-table tbody tr:last-child td{border-bottom:none}
.pw-dom{font-weight:600;color:var(--color-neutral-900);display:flex;align-items:center;gap:9px}
.pw-chip{display:inline-flex;align-items:center;font-size:11px;font-weight:700;padding:2px 9px;border-radius:999px;white-space:nowrap}
.pw-chip.ontstoppen{background:#e0f7fa;color:#0e7490}
.pw-chip.riool{background:#dbeafe;color:#1d4ed8}
.pw-chip.dienst{background:#ede9fe;color:#6d28d9}
.pw-chip.loodgieter{background:#dcfce7;color:#15803d}
.pw-chip.dakdekker{background:#fee2de;color:var(--color-brand-700)}
.pw-klant{color:var(--color-neutral-500);font-size:13px;text-align:left}
.pw-money{font-weight:700;color:var(--color-neutral-900)}
.pw-tnum{font-variant-numeric:tabular-nums}
.pw-mut{color:var(--color-neutral-300)}
.pw-former{color:var(--color-warning);font-weight:600;font-variant-numeric:tabular-nums;white-space:nowrap}
.pw-vs{display:inline-flex;align-items:center;gap:7px;font-weight:700;font-variant-numeric:tabular-nums}
.pw-vs.good{color:var(--color-success)}.pw-vs.warn{color:var(--color-warning)}.pw-vs.bad{color:var(--color-error)}
.pw-vsbar{width:44px;height:6px;border-radius:3px;background:var(--color-neutral-200);overflow:hidden}
.pw-vsbar i{display:block;height:100%;border-radius:3px}
.pw-roi{font-family:var(--font-mono);font-weight:600}.pw-roi.good{color:var(--color-success)}.pw-roi.warn{color:var(--color-warning)}
.pw-arrow{color:var(--color-neutral-300)}
.pw-table th.pw-sortable{cursor:pointer;user-select:none}
.pw-table th.pw-sortable:hover{color:var(--color-neutral-700)}
.pw-sortarr{color:var(--color-brand-600);font-size:10px;letter-spacing:0}
.pw-note{font-size:12px;color:var(--color-neutral-400);text-align:center;padding:16px}
.pw-back{display:inline-flex;align-items:center;gap:7px;font-size:13px;font-weight:600;color:var(--color-neutral-500);background:none;border:none;cursor:pointer;padding:6px 0;margin-bottom:12px}
.pw-back:hover{color:var(--color-brand-600)}
.pw-dhead{display:flex;align-items:center;gap:12px;margin-bottom:16px}
.pw-dhead h1{font-size:23px;font-weight:800;letter-spacing:-.02em;color:var(--color-neutral-900)}
.pw-callout{background:var(--surface);border:1px solid var(--color-neutral-100);border-left:4px solid var(--color-brand-600);border-radius:8px;padding:18px 22px;margin-bottom:18px;display:flex;gap:28px;flex-wrap:wrap;align-items:center}
.pw-lead{font-size:17px;font-weight:700;color:var(--color-neutral-900);line-height:1.4;flex:1;min-width:300px}
.pw-lead b.peak{color:var(--color-brand-600)}.pw-lead b.now{color:var(--color-error)}
.pw-stat{display:flex;flex-direction:column;gap:2px}
.pw-stat .k{font-size:10.5px;font-weight:700;color:var(--color-neutral-500);text-transform:uppercase;letter-spacing:.08em}
.pw-stat .v{font-family:var(--font-mono);font-size:19px;font-weight:600;color:var(--color-neutral-900)}
.pw-grid{display:grid;grid-template-columns:1.7fr 1fr;gap:18px}
@media(max-width:900px){.pw-grid{grid-template-columns:1fr}}
.pw-card{background:var(--surface);border:1px solid var(--color-neutral-100);border-radius:8px;padding:20px}
.pw-card h3{font-size:12px;font-weight:700;color:var(--color-neutral-600);text-transform:uppercase;letter-spacing:.07em;margin-bottom:14px;display:flex;align-items:center;justify-content:space-between;gap:10px}
.pw-src{font-size:10px;font-weight:600;color:var(--color-neutral-300);text-transform:none;letter-spacing:0}
.pw-legend{display:flex;gap:13px;font-size:11px;font-weight:600;color:var(--color-neutral-500)}
.pw-legend i{display:inline-block;width:9px;height:9px;border-radius:2px;margin-right:5px;vertical-align:middle}
.pw-funnel{display:flex;align-items:center;gap:4px;flex-wrap:wrap}
.pw-fstep{flex:1;min-width:90px;text-align:center;background:var(--color-neutral-50);border:1px solid var(--color-neutral-100);border-radius:6px;padding:12px 8px}
.pw-fv{font-family:var(--font-mono);font-size:20px;font-weight:600;color:var(--color-neutral-900)}
.pw-fk{font-size:11px;font-weight:700;color:var(--color-neutral-500);text-transform:uppercase;letter-spacing:.06em;margin-top:2px}
.pw-fsub{font-size:10px;font-weight:500;color:var(--color-neutral-400);margin-top:3px;line-height:1.3}
.pw-fsub.hl{color:var(--color-success);font-weight:700}
.pw-farrow{color:var(--color-neutral-300);font-size:18px;font-weight:700}
.pw-rk{display:flex;align-items:center;justify-content:space-between;padding:11px 0;border-bottom:1px solid var(--color-neutral-100)}
.pw-rk:last-child{border-bottom:none}
.pw-kw{font-weight:600;color:var(--color-neutral-800);font-size:14px}
.pw-kw small{display:block;color:var(--color-neutral-400);font-weight:500;font-size:11px}
.pw-rkurl{color:var(--color-info);text-decoration:none;font-variant-numeric:tabular-nums}
.pw-rkurl:hover{text-decoration:underline}
.pw-meta{text-align:right;font-size:13px;color:var(--color-neutral-500)}
.pw-meta b{color:var(--color-neutral-800)}
.pw-metrics{display:flex;gap:10px;flex-wrap:wrap;margin-bottom:16px}
.pw-metric{flex:1;min-width:78px;background:var(--color-neutral-50);border:1px solid var(--color-neutral-100);border-radius:6px;padding:10px 12px;text-align:center}
.pw-metric.hl{background:#d1fae5;border-color:#a7f3d0}
.pw-metric.hl .pw-mv{color:#15803d}
.pw-md{font-size:10px;font-weight:700;margin-top:2px;font-variant-numeric:tabular-nums}
.pw-md.up{color:#15803d}.pw-md.down{color:#b91c1c}
.pw-amETA{font-size:10.5px;color:var(--color-neutral-400);margin:-8px 0 14px}
.pw-ml{font-size:10px;font-weight:700;color:var(--color-neutral-500);text-transform:uppercase;letter-spacing:.05em}
.pw-mv{font-family:var(--font-mono);font-size:18px;font-weight:600;color:var(--color-neutral-900);margin-top:2px}
.pw-subh{font-size:10.5px;font-weight:700;color:var(--color-neutral-400);text-transform:uppercase;letter-spacing:.08em;margin:4px 0 8px}
.pw-comp{width:100%;border-collapse:separate;border-spacing:0;font-size:13px}
.pw-comp th{text-align:left;font-size:10px;font-weight:700;color:var(--color-neutral-400);text-transform:uppercase;letter-spacing:.06em;padding:6px 8px;border-bottom:1px solid var(--color-neutral-100)}
.pw-comp th.r{text-align:right}
.pw-comp td{padding:8px;border-bottom:1px solid var(--color-neutral-50);color:var(--color-neutral-700);font-variant-numeric:tabular-nums}
.pw-comp td.r{text-align:right}
.pw-comp tr:last-child td{border-bottom:none}
.pw-rktbl{width:100%;border-collapse:separate;border-spacing:0;font-size:13px}
.pw-rktbl th{text-align:left;font-size:10px;font-weight:700;color:var(--color-neutral-400);text-transform:uppercase;letter-spacing:.06em;padding:6px 10px;border-bottom:1px solid var(--color-neutral-100);white-space:nowrap}
.pw-rktbl th.r,.pw-rktbl td.r{text-align:right}.pw-rktbl th.c,.pw-rktbl td.c{text-align:center}
.pw-rktbl td{padding:8px 10px;border-bottom:1px solid var(--color-neutral-50);color:var(--color-neutral-700);vertical-align:middle}
.pw-rktbl tr:last-child td{border-bottom:none}
.pw-rkkw{font-weight:600;color:var(--color-neutral-900)}
.pw-pos{font-family:var(--font-mono);font-weight:700;font-variant-numeric:tabular-nums}
.pw-pos.good{color:var(--color-success)}.pw-pos.warn{color:var(--color-warning)}.pw-pos.bad{color:var(--color-error)}
.pw-rkmut{color:var(--color-neutral-300);font-size:12px}
/* ---- Rijke money-keyword tabel (pariteit met Money-Keywords) ---- */
.pw-kwtbl{width:100%;border-collapse:separate;border-spacing:0;font-size:13px}
.pw-kwtbl th{text-align:left;font-size:10px;font-weight:700;color:var(--color-neutral-500);text-transform:uppercase;letter-spacing:.06em;padding:9px 12px;border-bottom:2px solid var(--color-neutral-200);white-space:nowrap}
.pw-kwtbl th.r,.pw-kwtbl td.r{text-align:right}
.pw-kwtbl th.c,.pw-kwtbl td.c{text-align:center}
.pw-kwtbl td{padding:9px 12px;border-bottom:1px solid var(--color-neutral-100);color:var(--color-neutral-700);vertical-align:middle;font-variant-numeric:tabular-nums}
.pw-kwtbl tbody tr:hover{background:var(--row-hover)}
.pw-kwtbl tbody tr:last-child td{border-bottom:none}
.pw-poscell{display:flex;flex-direction:column;gap:2px;line-height:1.25}
.pw-posline{display:flex;align-items:baseline;gap:7px}
.pw-pagina{font-size:11.5px;color:var(--color-info);text-decoration:none;font-variant-numeric:tabular-nums;max-width:260px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.pw-pagina:hover{text-decoration:underline}
.pw-dlt{font-family:var(--font-mono);font-weight:700;font-size:11px;white-space:nowrap}
.pw-dlt.up{color:var(--color-success)}.pw-dlt.down{color:#e11d48}
.pw-dlt.new{color:var(--color-brand-600);font-size:13px}.pw-dlt.out{color:#b45309;font-size:12px;font-weight:800}
.pw-spark{display:block;vertical-align:middle;margin:0 auto}
.pw-conv{color:var(--color-neutral-500);font-size:12px;font-variant-numeric:tabular-nums}
.pw-upside{font-weight:700;color:var(--color-success);font-variant-numeric:tabular-nums}
.pw-diag{display:inline-block;font-size:10.5px;font-weight:700;padding:2px 9px;border-radius:999px;white-space:nowrap}
.pw-diag.onpage{background:#dcfce7;color:#15803d}
.pw-diag.linkgap{background:#fee2e2;color:#b91c1c}
.pw-diag.oos{background:var(--color-neutral-100);color:var(--color-neutral-500)}
.pw-weak{display:inline-block;margin-left:7px;font-size:10px;font-weight:700;padding:1px 6px;border-radius:999px;background:#d1fae5;color:#15803d}
.pw-scroll{overflow-x:visible}
.pw-swipe{display:none}
@media(max-width:768px){
  .pw-wrap{padding:16px 12px}
  .pw-top-in{padding:12px 14px}
  .pw-kpis{grid-template-columns:repeat(2,1fr);gap:10px;margin-bottom:14px}
  .pw-filters{gap:8px}
  .pw-search{min-width:0;flex:1 1 100%}
  .pw-sel{flex:1 1 calc(50% - 4px)}
  .pw-count{margin-left:0;flex:1 1 100%;text-align:right}
  .pw-scroll{overflow-x:auto;-webkit-overflow-scrolling:touch;overscroll-behavior-x:contain}
  .pw-table{min-width:720px}
  .pw-table td,.pw-table th{padding:10px 12px}
  .pw-chip{display:none}
  .pw-dom{font-size:13px}
  .pw-swipe{display:block;font-size:11px;color:var(--color-neutral-400);padding:8px 4px 2px;text-align:right}
  .pw-comp{min-width:520px}
  .pw-kwtbl{min-width:880px}
  .pw-kwtbl td,.pw-kwtbl th{padding:9px 10px}
  .pw-pagina{max-width:160px}
}
`;
